// =============================================================================
// GOUVERNANCE — Base de Connaissance (KB Manager)
// CRUD articles + preview + catégories
// =============================================================================
import { useState, useEffect } from "react";
import {
  BookOpen, Plus, Search, Pencil, Trash2, Save, X,
  ChevronRight, FolderOpen, Tag,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const KB_CATEGORIES = [
  { id: "general", label: "Général", color: "#94a3b8" },
  { id: "commercial", label: "Commercial", color: "var(--oh-stat-blue-text)" },
  { id: "conformite", label: "Conformité", color: "var(--oh-stat-amber-text)" },
  { id: "operations", label: "Opérations", color: "#10b981" },
  { id: "technique", label: "Technique", color: "var(--oh-stat-red-text)" },
  { id: "marketing", label: "Marketing", color: "#8b5cf6" },
  { id: "finance", label: "Finance", color: "var(--oh-stat-cyan-text)" },
  { id: "gouvernance", label: "Gouvernance", color: "var(--oh-stat-orange-text)" },
];

const KB_TARGET_PROFILES = [
  { id: "tous", label: "Tous les utilisateurs", description: "Visible par tout le monde" },
  { id: "installateur", label: "Installateurs", description: "Articles techniques chantier / dépose dossier" },
  { id: "visiteur", label: "Visiteurs AMO", description: "Articles terrain / DAL mobile" },
  { id: "stephanie", label: "Stéphanie (AMO TEF)", description: "Articles process interne TEF" },
  { id: "admin", label: "Admin tenant", description: "Articles d'administration" },
  { id: "super_admin", label: "Super Admin uniquement", description: "Articles sensibles platform owner" },
];

function getHeaders() {
  const token = localStorage.getItem("session_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function GouvKnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | article_id
  const [form, setForm] = useState({ title: "", content: "", category: "general", tags: "", target_profile: "tous" });

  useEffect(() => { loadArticles(); }, [filterCat]);

  const loadArticles = async (searchQ) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQ || search) params.set("search", searchQ || search);
      if (filterCat) params.set("category", filterCat);
      params.set("limit", "100");
      const res = await fetch(`${API}/api/support/kb/articles?${params}`, {
        headers: getHeaders(), credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setArticles(data.articles || []);
      }
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => loadArticles(search);

  const startNew = () => {
    setEditing("new");
    setForm({ title: "", content: "", category: "general", tags: "", target_profile: "tous" });
  };

  const startEdit = (article) => {
    setEditing(article.id);
    setForm({
      title: article.title,
      content: article.content,
      category: article.category || "general",
      tags: (article.tags || []).join(", "),
      target_profile: article.target_profile || "tous",
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ title: "", content: "", category: "general", tags: "", target_profile: "tous" });
  };

  const saveArticle = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Titre et contenu requis");
      return;
    }
    const payload = {
      title: form.title.trim(),
      content: form.content.trim(),
      category: form.category,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      target_profile: form.target_profile || "tous",
    };

    try {
      if (editing === "new") {
        const res = await fetch(`${API}/api/support/kb/articles`, {
          method: "POST", headers: getHeaders(), credentials: "include",
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          toast.success("Article créé");
          cancelEdit();
          loadArticles();
        } else {
          toast.error("Erreur de creation");
        }
      } else {
        const res = await fetch(`${API}/api/support/kb/articles/${editing}`, {
          method: "PUT", headers: getHeaders(), credentials: "include",
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          toast.success("Article mis a jour");
          cancelEdit();
          loadArticles();
        } else {
          toast.error("Erreur de mise a jour");
        }
      }
    } catch {
      toast.error("Erreur reseau");
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      const res = await fetch(`${API}/api/support/kb/articles/${articleId}`, {
        method: "DELETE", headers: getHeaders(), credentials: "include",
      });
      if (res.ok) {
        toast.success("Article supprime");
        loadArticles();
      }
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const catColor = (catId) => KB_CATEGORIES.find((c) => c.id === catId)?.color || "#94a3b8";
  const catLabel = (catId) => KB_CATEGORIES.find((c) => c.id === catId)?.label || catId;

  return (
    <div className="space-y-4" data-testid="gouv-kb-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-indigo-400" />
          <h2 className="text-lg font-bold text-white">Base de Connaissance</h2>
          <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
            {articles.length} article{articles.length !== 1 ? "s" : ""}
          </Badge>
        </div>
        <Button
          onClick={startNew}
          size="sm"
          className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs"
          data-testid="kb-new-article-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Nouvel article
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Rechercher un article..."
            className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-500"
            data-testid="kb-admin-search"
          />
          <Button onClick={handleSearch} variant="outline" size="sm" className="border-zinc-600" data-testid="kb-admin-search-btn">
            <Search className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => setFilterCat(null)}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              !filterCat ? "bg-indigo-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
            }`}
            data-testid="kb-admin-filter-all"
          >
            Tout
          </button>
          {KB_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setFilterCat(filterCat === cat.id ? null : cat.id)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                filterCat === cat.id ? "text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
              }`}
              style={filterCat === cat.id ? { background: cat.color } : {}}
              data-testid={`kb-admin-filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <div
          className="rounded-lg border p-4 space-y-3"
          style={{ background: "#1a1a2e", borderColor: "#6366f1" }}
          data-testid="kb-editor"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">
              {editing === "new" ? "Nouvel article" : "Modifier l'article"}
            </h3>
            <button onClick={cancelEdit} className="p-1 hover:bg-zinc-700 rounded" data-testid="kb-editor-cancel">
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Titre de l'article..."
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-500"
            data-testid="kb-editor-title"
          />
          <textarea
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            placeholder="Contenu de l'article (texte libre, instructions, FAQ...)"
            rows={8}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-500 resize-y"
            data-testid="kb-editor-content"
          />
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="text-xs text-zinc-500 mb-1 block">Catégorie</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm text-white focus:outline-none focus:border-indigo-500"
                data-testid="kb-editor-category"
              >
                {KB_CATEGORIES.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs text-zinc-500 mb-1 block">Tags (séparés par virgule)</label>
              <input
                type="text"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="tag1, tag2, tag3"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-500"
                data-testid="kb-editor-tags"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">
              Cible <span className="text-zinc-600">(qui voit cet article ?)</span>
            </label>
            <select
              value={form.target_profile}
              onChange={(e) => setForm({ ...form, target_profile: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm text-white focus:outline-none focus:border-indigo-500"
              data-testid="kb-editor-target-profile"
            >
              {KB_TARGET_PROFILES.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-zinc-500 mt-1">
              {KB_TARGET_PROFILES.find((p) => p.id === form.target_profile)?.description}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={cancelEdit} className="border-zinc-600 text-zinc-400">
              Annuler
            </Button>
            <Button size="sm" onClick={saveArticle} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="kb-editor-save">
              <Save className="w-3.5 h-3.5 mr-1" />
              {editing === "new" ? "Créer" : "Enregistrer"}
            </Button>
          </div>
        </div>
      )}

      {/* Articles List */}
      {loading ? (
        <p className="text-center text-zinc-500 text-sm py-8 animate-pulse">Chargement...</p>
      ) : articles.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">Aucun article</p>
          <p className="text-xs text-zinc-600 mt-1">Creez votre premier article pour alimenter la base de connaissance</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="kb-articles-admin-list">
          {articles.map((article) => (
            <div
              key={article.id}
              className="rounded-lg border p-4 hover:border-zinc-500 transition-colors group"
              style={{ background: "#111827", borderColor: "#27272a" }}
              data-testid={`kb-admin-article-${article.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ background: catColor(article.category) }}
                    />
                    <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: catColor(article.category) }}>
                      {catLabel(article.category)}
                    </span>
                    {article.target_profile && article.target_profile !== "tous" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
                        Cible : {KB_TARGET_PROFILES.find((p) => p.id === article.target_profile)?.label || article.target_profile}
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm font-semibold text-white mb-1">{article.title}</h4>
                  <p className="text-xs text-zinc-400 line-clamp-2">{article.content}</p>
                  {article.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {article.tags.map((tag) => (
                        <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-zinc-800 text-zinc-500">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button
                    onClick={() => startEdit(article)}
                    className="p-1.5 hover:bg-zinc-700 rounded transition-colors"
                    title="Modifier"
                    data-testid={`kb-edit-${article.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 text-zinc-400" />
                  </button>
                  <button
                    onClick={() => deleteArticle(article.id)}
                    className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
                    title="Supprimer"
                    data-testid={`kb-delete-${article.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
